/* @jsx h */
import {
  defineComponent,
  h,
  ref,
  onMounted,
  onUnmounted,
  VNode,
  Ref,
  watch,
  Teleport,
} from "vue";
import { HapticButton } from "../HapticButton";

export type ResponsiveNavbarProps = {
  renderDesktopNav: () => VNode | null;
  renderMobileNav: () => VNode | null;
  renderQuickLinks: () => VNode | null;
  renderLogo: () => VNode;
  isOpen: Ref<boolean>;
  rendreMobileShortcuts: () => VNode | null;
};

export const ResponsiveNavbar = defineComponent<ResponsiveNavbarProps>({
  name: "ResponsiveNavbar",
  props: [
    "renderDesktopNav",
    "renderMobileNav",
    "renderLogo",
    "renderQuickLinks",
    "isOpen",
    "rendreMobileShortcuts",
  ],
  setup(props) {
    const isOpen = props.isOpen;
    const sidebarRef = ref<HTMLDivElement | null>(null);
    const startX = ref(0);
    const isDragging = ref(false);
    const currentX = ref(0);

    const triggerHaptic = (duration = 18) => {
      if (typeof navigator !== "undefined" && navigator.vibrate) {
        navigator.vibrate(duration);
      }
    };

    const toggleSidebar = () => {
      triggerHaptic();
      isOpen.value = !isOpen.value;
    };

    watch(isOpen, (val) => {
      if (isOpen.value) {
        sidebarRef.value
          ? (sidebarRef.value.style.transform = "translateX(0%)")
          : null;
      } else {
        sidebarRef.value
          ? (sidebarRef.value.style.transform = "translateX(100%)")
          : null;
      }
    });

    const handleTouchStart = (e: TouchEvent) => {
      if (!sidebarRef.value) return;
      startX.value = e.touches[0].clientX;
      isDragging.value = true;
      currentX.value = 0;
      if (sidebarRef.value) {
        sidebarRef.value.style.transition = "none";
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging.value || !sidebarRef.value) return;
      const deltaX = e.touches[0].clientX - startX.value;
      if (!isOpen.value && deltaX < 0) return;
      if (isOpen.value && deltaX > 0) {
        currentX.value = deltaX;
        sidebarRef.value.style.transform = `translateX(${deltaX}px)`;
      } else if (!isOpen.value && deltaX < 0) {
        currentX.value = deltaX;
        sidebarRef.value.style.transform = `translateX(calc(100% + ${deltaX}px))`;
      }
    };

    const handleTouchEnd = () => {
      if (!sidebarRef.value || !isDragging.value) return;
      isDragging.value = false;
      sidebarRef.value.style.transition = "transform 0.15s ease-out";

      if (Math.abs(currentX.value) > 100) {
        triggerHaptic(28);
        isOpen.value = !isOpen.value;
      }

      sidebarRef.value.style.transform = isOpen.value
        ? "translateX(0)"
        : "translateX(100%)";
      currentX.value = 0;
    };

    onMounted(() => {
      if (sidebarRef.value) {
        sidebarRef.value.style.transform = isOpen.value
          ? "translateX(0)"
          : "translateX(100%)";
      }
    });

    onUnmounted(() => {
      document.body.style.overflow = "auto";
    });

    return () => (
      <>
        {/* Main Navbar */}
        <nav class="pl-2 pr-2 2xl:pl-4 2xl:pr-4 flex items-center  sticky top-0">
          {/* Logo */}
          <div class="flex flex-1 items-center">{props.renderLogo()}</div>

          {/* Desktop Navigation */}
          <div class="hidden lg:block">{props.renderDesktopNav()}</div>

          <div class="lg:hidden">{props.rendreMobileShortcuts()}</div>
          {/* Mobile Menu Button */}
          <HapticButton
            class="lg:hidden text-accent btn btn-xs w-10 h-10"
            onClick={toggleSidebar}
          >
            <span class="material-symbols-outlined text-2xl">
              {isOpen.value ? "close" : "menu"}
            </span>
          </HapticButton>
          <div class="hidden lg:flex flex-1 justify-end">
            {props.renderQuickLinks()}
          </div>
        </nav>

        {/* Mobile Sidebar */}
        {isOpen.value && (
          <div
            class="fixed inset-0 bg-black/35 backdrop-blur-[1px] lg:hidden hidden"
            onClick={toggleSidebar}
          />
        )}
        <Teleport to="body">
          <div
            ref={sidebarRef}
            class="fixed top-0 bottom-2 right-0 w-full max-w-[300px] glossy-bg  border-l  flex flex-col l-box-shadow rounded-l-lg lg:hidden"
            onTouchstart={handleTouchStart}
            onTouchmove={handleTouchMove}
            onTouchend={handleTouchEnd}
            style={{
              transition: "transform 0.15s ease-out",
              transform: isOpen.value ? "translateX(0)" : "translateX(100%)",
              //zIndex: 9999,
              paddingTop: `var(--safe-area-top, 0px)`,
            }}
          >
            <div class="flex py-3 px-1 border-b ">
              <>{props.renderQuickLinks()}</>
            </div>
            <div class="py-2 flex flex-col flex-1">
              {props.renderMobileNav()}
            </div>
          </div>
        </Teleport>
      </>
    );
  },
});
